import * as THREE from "three";
import { DrawingModes, ObstacleTypes, POLYGONS, TREESPHERES, VARS } from "../Global/variables";
import SegmentManager from "../Segments/SegmentManager";
import ObstacleManager from "../Obstacles/ObstacleManager";
import PanelManager from "../Panels/PanelManager";
import KeyEventListener from "./KeyEventListener";
import RulerManager from "../Ruler/RulerManager";
import { OBSTACLE, COLORS } from "../Global/materials";
import { selection } from "./Helpers";
import { updateSegmentHover, clearSegmentHover } from "../Segments/Functions";
import { checkPanelsOK, removeGrid, selectPanelsByIndices } from "../Panels/PanelFunctions";
import { pushUndoAction } from "../Global/UndoManager";
import { findCenter } from "../Obstacles/Shapes";
import { deleteObstacle, selectObstacle } from "../Obstacles/ObstacleFunctions";
import { createSegmentPlane } from "../Global/functions";
import { createTrimSphereMesh } from "../Trees/treeDrawingUtils";
import { setPanelWireframeStyle } from "../Panels/Helpers";
import { gridHelper, panelInSegment } from "../Panels/Placement";
import { isPanelPastePreviewActive, pasteCopiedPanels } from "../Panels/PanelCopyPaste";
import { clearRulerHover, clearRulerSelection, selectRulerAt, updateRulerHover } from "../Ruler/RulerStore";
import { notifyIrradianceNeedsRecompute } from "../Shading/IrradianceEvents";

export class MouseEventHandlers {
  constructor(SceneManager, currentDrawingModeRef, obstacleTypeRef, updateDrawingMode, polygons,
    setPolygons, setObstacles, setDeleteObstacle, setDeleteSegment, setPanels, setDeletePanels, deletePanels, setDeleteConfirmationVisible, selectedIndices, setSelectedIndices,
    setPolygonDetails, modelMeshes, solarPanel, solarPanelRef, setSolarPanel, setShowPanelSettings, showPanelSettingsRef, mouseDown, setMouseDown, panelList,
    arePanelsOK, setPanelsOK, setRemovedSections) {
    
    // Initialize properties for managing mouse interaction and drawing state
    this.mousePosition = new THREE.Vector2(); // Current mouse position in normalized device coordinates
    this.mouseDown = mouseDown; // State of mouse button (pressed/released)
    this.setMouseDown = setMouseDown; // Function to update mouseDown state
    this.mouseMoved = false; // Flag to track if mouse has moved
    this.sceneManager = SceneManager; // Scene manager to manage 3D scene
    this.currentDrawingModeRef = currentDrawingModeRef;
    this.currentDrawingMode = currentDrawingModeRef.current; // Current drawing mode (e.g., segment, obstacle)
    this.updateDrawingMode = updateDrawingMode; // Function to update the drawing mode
    this.Mesh = modelMeshes; // 3D model meshes to detect intersections
    this.isDrawingStarted = false; // Flag to track whether drawing has started
    this.shape = null; // Shape manager instance (e.g., for segments, obstacles)
    this.obstacleTypeRef = obstacleTypeRef;
    this.obstacleType = obstacleTypeRef.current; // Current obstacle type (e.g., rectangle, circle)
    this.start = new THREE.Vector2(); // Starting point for drawing shapes
    this.end = new THREE.Vector2(); // Ending point for drawing shapes
    this.polygonsState = polygons; // State of polygons (shapes being drawn)
    this.setPolygons = setPolygons; // Function to update polygons state
    this.setObstacles = setObstacles; // Function to update obstacles state
    this.setDeleteObstacle = setDeleteObstacle; // Function to delete obstacles
    this.setDeleteSegment = setDeleteSegment; // Function to delete segments
    this.setPanels = setPanels; // Function to update panels
    this.setDeletePanels = setDeletePanels; // Function to delete panels
    this.deletePanels = deletePanels; // Panel deletion logic
    this.selectedIndices = selectedIndices; // Selected indices for panels or obstacles
    this.setSelectedIndices = setSelectedIndices; // Function to update selected indices
    this.setPolygonDetails = setPolygonDetails; // Function to update polygon details
    this.setShowPanelSettings = setShowPanelSettings; // Function to show/hide panel settings
    this.showPanelSettingsRef = showPanelSettingsRef; // Ref to whether panel settings is open
    this.solarPanel = solarPanel; // Solar panel state
    this.solarPanelRef = solarPanelRef; // Ref to latest solar panel state
    this.setSolarPanel = setSolarPanel; // Function to set solar panel state
    this.panelList = panelList; // List of panels in the scene
    this.panelTexture = null; // Panel texture (if any)
    this.arePanelsOK = arePanelsOK; // Whether panels are correctly positioned
    this.setPanelsOK = setPanelsOK; // Function to set panels OK state
    this.obsPos = null; // Position of obstacles for dragging
    this.panelDragSnapshot = null;
    this.positions = null;
    this.panelBoxSelection = null;
    this.panelBoxElement = null;
    this.setRemovedSections = setRemovedSections;
    this.trimStartPoint = null;
    this.trimSphere = null;

    // Handle shape completion (e.g., for segment drawing)
    const handleCompleteShape = () => {
      this.shape.completeShape(selectedIndices);
    }

    // Undo and redo functions for shape manipulation
    function undo() {
      this.shape.undo();
    }

    function redo() {
      this.shape.redo();
    }

    // Function to cancel segment drawing
    const cancelSegmentDrawing = () => {
      this.stopDrawing();
    }

    // Function to cancel drawing any shape (rectangle, circle, etc.)
    const cancelDrawing = () => {
      if (obstacleTypeRef.current === ObstacleTypes.RECTANGLE) {
        OBSTACLE.box.visible = false;
        this.stopDrawing();
      } else if (obstacleTypeRef.current === ObstacleTypes.CIRCLE) {
        OBSTACLE.circle.visible = false;
        this.stopDrawing();
      } else if (obstacleTypeRef.current === ObstacleTypes.POLYGON) {
        OBSTACLE.box.visible = false;
        this.stopDrawing();
      }
    }

    // Function to remove ruler when drawing
    function removeRuler() {
      if (this.shape) {
        this.shape.removeRuler();
      }
      this.stopDrawing();
    }

    // Create a new key listener or update the existing one
    this.keyListener = VARS.keyListener;
    if (this.keyListener === undefined || this.keyListener == null) {
      this.keyListener = new KeyEventListener(
        handleCompleteShape.bind(this),
        this.currentDrawingMode,
        undo.bind(this),
        redo.bind(this),
        setDeleteConfirmationVisible.bind(this),
        this.selectedIndices,
        cancelDrawing.bind(this),
        removeRuler.bind(this),
        cancelSegmentDrawing.bind(this),
        this.updateDrawingMode,
        this.setDeleteObstacle,
        this.setDeleteSegment,
        this.setDeletePanels,
        this.setSelectedIndices,
        this.setShowPanelSettings
      );
    } else {
      this.keyListener.completeShape = handleCompleteShape.bind(this);
      this.keyListener.currentDrawingMode = this.currentDrawingMode;
      this.keyListener.undo = undo.bind(this);
      this.keyListener.redo = redo.bind(this);
      this.keyListener.selectedindices = this.selectedIndices;
      this.keyListener.setSelectedIndices = this.setSelectedIndices;
      this.keyListener.setShowPanelSettings = this.setShowPanelSettings;
      this.keyListener.setDeleteObstacle = this.setDeleteObstacle;
      this.keyListener.setDeleteSegment = this.setDeleteSegment;
      this.keyListener.setDeletePanels = this.setDeletePanels;
      this.keyListener.canceldrawing = cancelDrawing.bind(this);
      this.keyListener.removeRuler = removeRuler.bind(this);
      this.keyListener.cancelSegmentDrawing = cancelSegmentDrawing.bind(this);
    }
    VARS.keyListener = this.keyListener;
  }

  getDrawingMode() {
    return this.currentDrawingModeRef?.current ?? this.currentDrawingMode;
  }

  getObstacleType() {
    return this.obstacleTypeRef?.current ?? this.obstacleType;
  }

  isPanelBoxSelectEnabled() {
    const mode = this.getDrawingMode();
    if (mode !== DrawingModes.NONE) return false;
    if (this.selectedIndices?.segmentIndex == null) return false;
    return this.showPanelSettingsRef?.current === true;
  }

  isIrradianceBusy() {
    return typeof window !== "undefined" && window.isIrradianceBusy === true;
  }

  buildRaycaster() {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(this.mousePosition, this.sceneManager.camera);
    return raycaster;
  }

  isPointerOnPanelInSelectedSegment() {
    const segmentIndex = this.selectedIndices?.segmentIndex;
    if (segmentIndex == null) return false;

    const segmentPanels = POLYGONS?.[segmentIndex]?.panels ?? [];
    if (!segmentPanels.length) return false;

    const panels = segmentPanels.map((entry) => entry?.panel).filter(Boolean);
    if (!panels.length) return false;

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(this.mousePosition, this.sceneManager.camera);
    const intersects = raycaster.intersectObjects(panels, false);
    return intersects.length > 0;
  }

  ensurePanelBoxElement() {
    if (this.panelBoxElement && this.panelBoxElement.isConnected) return this.panelBoxElement;

    const existing = document.getElementById("panel-selection-box");
    if (existing) {
      this.panelBoxElement = existing;
      return existing;
    }

    const element = document.createElement("div");
    element.id = "panel-selection-box";
    element.style.position = "fixed";
    element.style.pointerEvents = "none";
    element.style.border = "1px solid rgba(250, 214, 89, 0.95)";
    element.style.background = "rgba(250, 214, 89, 0.2)";
    element.style.boxShadow = "inset 0 0 0 1px rgba(250, 214, 89, 0.35)";
    element.style.zIndex = "30";
    element.style.display = "none";

    document.body.appendChild(element);
    this.panelBoxElement = element;
    return element;
  }

  hidePanelBoxElement() {
    if (!this.panelBoxElement) return;
    this.panelBoxElement.style.display = "none";
  }

  startPanelBoxSelection(event) {
    this.panelBoxSelection = {
      active: true,
      dragStarted: false,
      startX: event.clientX,
      startY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY,
      threshold: 5,
    };

    const box = this.ensurePanelBoxElement();
    box.style.display = "none";
    box.style.left = `${event.clientX}px`;
    box.style.top = `${event.clientY}px`;
    box.style.width = "0px";
    box.style.height = "0px";
  }

  updatePanelBoxSelection(event) {
    const state = this.panelBoxSelection;
    if (!state?.active) return;

    state.currentX = event.clientX;
    state.currentY = event.clientY;

    const width = Math.abs(state.currentX - state.startX);
    const height = Math.abs(state.currentY - state.startY);

    if (!state.dragStarted && (width >= state.threshold || height >= state.threshold)) {
      state.dragStarted = true;
    }

    const box = this.ensurePanelBoxElement();
    if (!state.dragStarted) {
      box.style.display = "none";
      return;
    }

    const left = Math.min(state.startX, state.currentX);
    const top = Math.min(state.startY, state.currentY);

    box.style.display = "block";
    box.style.left = `${left}px`;
    box.style.top = `${top}px`;
    box.style.width = `${width}px`;
    box.style.height = `${height}px`;
  }

  getPanelScreenBounds(panel, canvasRect) {
    const positionAttribute = panel?.geometry?.attributes?.position;
    if (!positionAttribute) return null;

    panel.updateMatrixWorld(true);

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < positionAttribute.count; i++) {
      const world = new THREE.Vector3().fromBufferAttribute(positionAttribute, i);
      world.applyMatrix4(panel.matrixWorld);
      const projected = world.project(this.sceneManager.camera);

      const x = ((projected.x + 1) * 0.5) * canvasRect.width + canvasRect.left;
      const y = ((1 - projected.y) * 0.5) * canvasRect.height + canvasRect.top;
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
      return null;
    }

    return { left: minX, right: maxX, top: minY, bottom: maxY };
  }

  getPanelIndicesInClientRect(selectionRect) {
    const segmentIndex = this.selectedIndices?.segmentIndex;
    if (segmentIndex == null) return [];

    const segmentPanels = POLYGONS?.[segmentIndex]?.panels ?? [];
    if (!segmentPanels.length) return [];

    const canvasRect = this.sceneManager?.renderer?.domElement?.getBoundingClientRect?.();
    if (!canvasRect) return [];

    const intersects = (a, b) =>
      !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

    const selected = [];
    for (let i = 0; i < segmentPanels.length; i++) {
      const panel = segmentPanels[i]?.panel;
      if (!panel) continue;

      const bounds = this.getPanelScreenBounds(panel, canvasRect);
      if (!bounds) continue;
      if (intersects(bounds, selectionRect)) {
        selected.push(i);
      }
    }

    return selected;
  }

  setSolarPanelFromIndex(panelIndex) {
    const segmentIndex = this.selectedIndices?.segmentIndex;
    if (segmentIndex == null || panelIndex == null) return;

    const uiPanel = this.polygonsState?.[segmentIndex]?.panels?.[panelIndex] || {};
    const polygonPanel = POLYGONS?.[segmentIndex]?.panels?.[panelIndex] || null;
    const panelTransform = polygonPanel?.panel?.userData?.panelTransform || {};

    this.setSolarPanel({
      ...uiPanel,
      id: polygonPanel?.id ?? uiPanel?.id ?? null,
      panel_id: polygonPanel?.panel?.id ?? uiPanel?.panel_id ?? null,
      tilt: uiPanel?.tilt ?? panelTransform.tilt ?? 0,
      rotation: uiPanel?.rotation ?? panelTransform.rotation ?? 0,
      azimuth: uiPanel?.azimuth ?? panelTransform.azimuth ?? 0,
    });
  }

  finishPanelBoxSelection(event) {
    const state = this.panelBoxSelection;
    if (!state?.active) return;

    this.hidePanelBoxElement();
    this.panelBoxSelection = null;

    if (!state.dragStarted) {
      selection(
        this.selectedIndices,
        this.setSelectedIndices,
        this.setPolygonDetails,
        this.mousePosition,
        this.sceneManager,
        this.setShowPanelSettings,
        this.setSolarPanel,
        this.polygonsState,
        event?.shiftKey === true
      );
      return;
    }

    const rect = {
      left: Math.min(state.startX, state.currentX),
      right: Math.max(state.startX, state.currentX),
      top: Math.min(state.startY, state.currentY),
      bottom: Math.max(state.startY, state.currentY),
    };

    const panelIndices = this.getPanelIndicesInClientRect(rect);
    selectPanelsByIndices(
      panelIndices,
      this.setSelectedIndices,
      this.selectedIndices,
      this.setShowPanelSettings
    );

    if (panelIndices.length > 0) {
      this.setSolarPanelFromIndex(panelIndices[0]);
    } else {
      this.setSolarPanel((prev) => ({ ...(prev || {}), panel_id: null }));
    }
  }

  ensurePanelManager() {
    const isValidManager =
      this.shape &&
      !Array.isArray(this.shape) &&
      typeof this.shape.handleMouseMove === "function" &&
      typeof this.shape.handleMouseDown === "function";

    if (!isValidManager) {
      const solarPanelData = this.solarPanelRef?.current ?? this.solarPanel;
      this.shape = new PanelManager(
        this.sceneManager.scene,
        this.sceneManager.camera,
        this.selectedIndices.segmentIndex,
        solarPanelData,
        this.setPanels,
        this.panelList,
        null,
        null,
        false,
        this.setPolygons
      );
      this.isDrawingStarted = true;
    } else if (!this.isDrawingStarted) {
      this.isDrawingStarted = true;
    }
  }

  getSegmentPlaneIntersection(segmentIndex) {
    if (segmentIndex == null) return null;
    const plane = createSegmentPlane(segmentIndex);
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(this.mousePosition, this.sceneManager.camera);
    const intersection = new THREE.Vector3();
    const hit = raycaster.ray.intersectPlane(plane, intersection);
    return hit ? intersection : null;
  }

  updateDraggedPanelsWireframe(segmentIndex, panelIndices, options = {}) {
    if (segmentIndex == null || !Array.isArray(panelIndices)) return;
    const isDragging = options?.isDragging === true;
    this.sceneManager?.scene?.updateMatrixWorld?.(true);

    for (const index of panelIndices) {
      const panel = POLYGONS?.[segmentIndex]?.panels?.[index]?.panel;
      if (!panel) continue;
      panel.updateMatrixWorld?.(true);

      if (!isDragging) {
        // Static selected state is always yellow.
        setPanelWireframeStyle(panel, {
          opacity: 1,
          color: COLORS.yellow,
        });
        if (panel.material?.emissive?.set) {
          panel.material.emissive.set(COLORS.yellow);
          panel.material.emissiveIntensity = 0.25;
        }
        continue;
      }

      const valid = panelInSegment(panel, segmentIndex);
      setPanelWireframeStyle(panel, {
        opacity: 1,
        color: valid ? COLORS.yellow : COLORS.red,
      });
      if (panel.material?.emissive?.set) {
        panel.material.emissive.set(valid ? COLORS.yellow : COLORS.red);
        panel.material.emissiveIntensity = valid ? 0.25 : 0.18;
      }
    }
  }

  clearPanelDragGrid(snapshot = this.panelDragSnapshot) {
    if (!snapshot?.grid) return;
    if (snapshot.grid.parent) {
      snapshot.grid.parent.remove(snapshot.grid);
    } else if (snapshot.gridHost) {
      snapshot.gridHost.remove(snapshot.grid);
    }
    snapshot.grid.geometry?.dispose?.();
    snapshot.grid.material?.dispose?.();
    snapshot.grid = null;
    snapshot.gridHost = null;
  }

  // Mouse down event handler
  async handleMouseDown(event) {
    if (event.target === document.querySelector('canvas')) {
      // OrbitControls owns Ctrl/Command + primary-button drags for camera
      // panning. Do not let the editor begin a select/draw action as well.
      if (event.button === 0 && (event.ctrlKey || event.metaKey)) return;

      const mode = this.getDrawingMode();
      this.currentDrawingMode = mode;
      this.setMouseDown(true); // Update mouse down state
      this.mouseDown = true; // Track mouse down state
      if (event.which === 1 || event.button === 0) {
        this.getMousePosition(event); // Get mouse position in normalized device coordinates

        if (mode === DrawingModes.NONE && isPanelPastePreviewActive()) {
          event.preventDefault();
          pasteCopiedPanels({
            setPolygons: this.setPolygons,
            setSelectedIndices: this.setSelectedIndices,
            setSolarPanel: this.setSolarPanel,
            setPanelsOK: this.setPanelsOK,
            arePanelsOK: this.arePanelsOK,
          });
          return;
        }

        // A finished ruler sits on top of everything (depthTest off), so it gets
        // first refusal on the click before segment/panel/obstacle selection.
        if (mode === DrawingModes.NONE) {
          if (selectRulerAt(this.buildRaycaster(), this.sceneManager.camera)) {
            event.preventDefault();
            return;
          }
          clearRulerSelection();
        }

        if (mode === DrawingModes.NONE && this.isPanelBoxSelectEnabled() && !this.isPointerOnPanelInSelectedSegment()) {
          event.preventDefault();
          this.startPanelBoxSelection(event);
          return;
        }

        let intersections = this.getIntersections(); // Get intersection points with 3D objects
        if (intersections.length > 0) {
          let point = intersections[0].point; // Get the first intersection point

          if (
            this.isIrradianceBusy() &&
            [DrawingModes.SEGMENT, DrawingModes.OBSTACLE, DrawingModes.PANEL, DrawingModes.TRIM, DrawingModes.RULER].includes(mode)
          ) {
            return;
          }

          switch (mode) {
            // Handle drawing segments
            case DrawingModes.SEGMENT:
              if (!this.isDrawingStarted) {
                this.isDrawingStarted = true; // Mark drawing as started
                if (this.shape == null) {
                  this.shape = new SegmentManager(
                    this.sceneManager.scene,
                    this.stopDrawing,
                    this.setPolygons,
                    this.selectedIndices,
                    this.setSelectedIndices,
                    this.setPolygonDetails
                  );
                }
              }
              this.shape.handleMouseDown(point); // Handle mouse down for segment
              break;

            // Handle drawing obstacles
            case DrawingModes.OBSTACLE:
              if (!this.isDrawingStarted) {
                this.isDrawingStarted = true;
                this.shape = new ObstacleManager(
                  this.sceneManager.scene,
                  this.sceneManager.camera,
                  this.getObstacleType(),
                  this.stopDrawing,
                  this.setObstacles,
                  this.selectedIndices,
                  null,
                  this.setPolygons
                );
              }
              this.start = this.getPoints(this.start); // Set starting point for obstacle
              this.shape.handleMouseDown(this.start); // Handle mouse down for obstacle
              break;

            // Handle ruler drawing
            case DrawingModes.RULER:
              if (!this.isDrawingStarted) {
                this.isDrawingStarted = true;
                this.shape = new RulerManager(
                  this.sceneManager.scene,
                  this.stopDrawing
                );
              }
              this.shape.handleMouseDown(point); // Handle mouse down for ruler
              break;

            // Handle panel drawing
            case DrawingModes.PANEL: {
              this.ensurePanelManager();

              if (this.shape?.panel === null || this.shape?.panel === undefined) {
                await this.shape.handleMouseMove(this.mousePosition);
              }

              if (this.shape) {
                this.shape.handleMouseDown(this.setPanelsOK);
              }
              break;
            }

            case DrawingModes.TRIM:
              this.startTrimSphere(point);
              break;

            // Handle none drawing mode (e.g., selection)
            case DrawingModes.NONE:
              selection(
                this.selectedIndices,
                this.setSelectedIndices,
                this.setPolygonDetails,
                this.mousePosition,
                this.sceneManager,
                this.setShowPanelSettings,
                this.setSolarPanel,
                this.polygonsState,
                event?.shiftKey === true
              );
              break;
          }
        }
      }
    }
  }

  // Mouse move event handler
  handleMouseMove(event) {
    this.getMousePosition(event); // Update mouse position

    if (this.panelBoxSelection?.active) {
      this.updatePanelBoxSelection(event);
      return;
    }

    
    const canvasEl = this.sceneManager?.renderer?.domElement;
    const overCanvas =
      !!canvasEl &&
      (event.target === canvasEl ||
        (typeof document !== "undefined" &&
          document.getElementById("canvas")?.contains(event.target)));
    if (overCanvas) {
      if (this.getDrawingMode() === DrawingModes.NONE && !VARS.isVertexDragActive) {
        const hoverRay = new THREE.Raycaster();
        hoverRay.setFromCamera(this.mousePosition, this.sceneManager.camera);
        updateSegmentHover(hoverRay, this.selectedIndices?.segmentIndex ?? null);
        // Hovering a finished ruler expands its label to show pitch/rise/run.
        updateRulerHover(hoverRay, this.sceneManager.camera);
      } else {
        clearSegmentHover(this.selectedIndices?.segmentIndex ?? null);
        clearRulerHover();
      }
    }

    let intersections = this.getIntersections(); // Get intersection points with 3D objects
    if (intersections.length > 0) {
      let point = intersections[0].point; // Get the first intersection point
      const mode = this.getDrawingMode();
      this.currentDrawingMode = mode;
      switch (mode) {
        // Handle moving segments
        case DrawingModes.SEGMENT:
          if (this.isIrradianceBusy()) break;
          if (this.shape == null) {
            this.shape = new SegmentManager(
              this.sceneManager.scene,
              this.stopDrawing,
              this.setPolygons,
              this.selectedIndices,
              this.setSelectedIndices,
              this.setPolygonDetails
            );
          }
          this.shape.handleMouseMove(point); // Handle mouse move for segment
          break;

        // Handle moving obstacles
        case DrawingModes.OBSTACLE:
          if (this.isIrradianceBusy()) break;
          if (this.isDrawingStarted) {
            this.end = this.getPoints(this.end); // Update endpoint for obstacle
            this.shape.handleMouseMove(this.end); // Handle mouse move for obstacle
          }
          break;

        // Handle moving ruler
        case DrawingModes.RULER:
          if (this.isIrradianceBusy()) break;
          if (this.isDrawingStarted) {
            this.shape.handleMouseMove(point); // Handle mouse move for ruler
          }
          break;

        // Handle moving panels
        case DrawingModes.PANEL:
              if (this.isIrradianceBusy()) break;
              this.ensurePanelManager();
              this.shape?.handleMouseMove(this.mousePosition); // Handle mouse move for panel
              break;

        case DrawingModes.TRIM:
          if (this.isIrradianceBusy()) break;
          this.updateTrimSphere(point);
          break;

        // Handle selection and movement of existing panels or obstacles
        case DrawingModes.NONE:
        if (this.isIrradianceBusy()) {
          break;
        }
        if (
          this.mouseDown &&
          event.which === 1 &&
          Array.isArray(this.selectedIndices.panelIndex) &&
          this.selectedIndices.panelIndex?.length > 0
        ) {            
          if (!this.isDrawingStarted) {
              const dragAnchor = this.getSegmentPlaneIntersection(this.selectedIndices.segmentIndex);
              if (!dragAnchor) {
                break;
              }
              this.isDrawingStarted = true;
              this.panelDragSnapshot = {
                segmentIndex: this.selectedIndices.segmentIndex,
                panelIndices: [...this.selectedIndices.panelIndex],
                panels: [],
                startPositions: [],
                dragAnchorStart: dragAnchor.clone(),
                selectedSet: new Set(this.selectedIndices.panelIndex),
                grid: null,
                gridHost: null,
              };
              for (let index of this.selectedIndices.panelIndex) {
                const panel = POLYGONS[this.selectedIndices.segmentIndex].panels[index].panel;
                this.panelDragSnapshot.panels.push(panel);
                this.panelDragSnapshot.startPositions.push(panel.position.clone());
              }
            }

            const snap = this.panelDragSnapshot;
            const currentIntersection = this.getSegmentPlaneIntersection(snap?.segmentIndex);
            if (!snap || !currentIntersection) {
              break;
            }
            const delta = currentIntersection.clone().sub(snap.dragAnchorStart);
            snap.panels.forEach((panel, i) => {
              if (!panel) return;
              panel.position.copy(snap.startPositions[i].clone().add(delta));
            });

            // Snap as a group using the first selected panel as anchor.
            this.clearPanelDragGrid(snap);
            if (snap.panels.length > 0) {
              const anchorPanel = snap.panels[0];
              const segmentPanels = POLYGONS?.[snap.segmentIndex]?.panels ?? [];
              const nonSelectedPanels = segmentPanels.filter(
                (_, idx) => !snap.selectedSet?.has(idx)
              );
              if (nonSelectedPanels.length > 0) {
                const [grid, snappedPosition, gridHost] = gridHelper(anchorPanel, nonSelectedPanels);
                if (grid && gridHost) {
                  gridHost.add(grid);
                  snap.grid = grid;
                  snap.gridHost = gridHost;
                }
                if (snappedPosition) {
                  const snapDelta = snappedPosition.clone().sub(anchorPanel.position);
                  snap.panels.forEach((panel) => {
                    if (!panel) return;
                    panel.position.add(snapDelta);
                  });
                }
              }
            }

           
            this.updateDraggedPanelsWireframe(snap.segmentIndex, snap.panelIndices, {
              isDragging: true,
            });

            const movedNow = snap.startPositions.some((pos, i) => {
              const panel = snap.panels[i];
              return panel && panel.position.distanceToSquared(pos) > 1e-10;
            });
            this.mouseMoved = this.mouseMoved || movedNow;
          }
          
          break;
        default:
          break;
      }
    }
  }

  handleMouseUp(event) {
    if (this.isIrradianceBusy()) {
      this.setMouseDown(false);
      this.mouseDown = false;
      this.mouseMoved = false;
      this.clearPanelDragGrid();
      this.panelDragSnapshot = null;
      if (this.getDrawingMode() === DrawingModes.TRIM) {
        this.cancelTrimSphere();
      }
      return;
    }

    this.setMouseDown(false);
    this.mouseDown = false;

    if (this.panelBoxSelection?.active) {
      this.getMousePosition(event);
      this.finishPanelBoxSelection(event);
      this.panelDragSnapshot = null;
      this.mouseMoved = false;
      return;
    }

    if (this.mouseMoved && this.selectedIndices.panelIndex !== null && this.selectedIndices.panelIndex?.length > 0) {
      const snap = this.panelDragSnapshot;
      if (snap && Array.isArray(snap.panels) && snap.panels.length) {
        this.clearPanelDragGrid(snap);

        this.stopDrawing();
        removeGrid();

        const finalPositions = snap.panels.map((p) => p.position.clone());
        const moved = snap.startPositions.some((pos, i) => {
          const end = finalPositions[i];
          return end && pos && end.distanceToSquared(pos) > 1e-8;
        });

        if (moved) {
          const segmentIndex = snap.segmentIndex;
          const setPanelsOK = this.setPanelsOK;
          const arePanelsOK = this.arePanelsOK;
          const hasInvalidPlacement = snap.panels.some(
            (panel) => !panelInSegment(panel, segmentIndex)
          );

          if (hasInvalidPlacement) {
            snap.panels.forEach((panel, i) => {
              if (!panel) return;
              panel.position.copy(snap.startPositions[i]);
            });
            this.updateDraggedPanelsWireframe(segmentIndex, snap.panelIndices, {
              isDragging: false,
            });
            if (segmentIndex != null && typeof setPanelsOK === "function") {
              checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
            }
            this.mouseMoved = false;
            this.panelDragSnapshot = null;
            return;
          }

          this.updateDraggedPanelsWireframe(segmentIndex, snap.panelIndices, {
            isDragging: false,
          });
          if (segmentIndex != null && typeof setPanelsOK === "function") {
            checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
          }

          pushUndoAction({
            label: snap.panels.length > 1 ? "Move panels" : "Move panel",
            undo: () => {
              snap.panels.forEach((panel, i) => {
                if (!panel) return;
                panel.position.copy(snap.startPositions[i]);
              });
              this.updateDraggedPanelsWireframe(segmentIndex, snap.panelIndices, {
                isDragging: false,
              });
              if (segmentIndex != null && typeof setPanelsOK === "function") {
                checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
              }
            },
            redo: () => {
              snap.panels.forEach((panel, i) => {
                if (!panel) return;
                panel.position.copy(finalPositions[i]);
              });
              this.updateDraggedPanelsWireframe(segmentIndex, snap.panelIndices, {
                isDragging: false,
              });
              if (segmentIndex != null && typeof setPanelsOK === "function") {
                checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
              }
            },
          });
        } else {
          this.updateDraggedPanelsWireframe(snap.segmentIndex, snap.panelIndices, {
            isDragging: false,
          });
          if (snap.segmentIndex != null && typeof this.setPanelsOK === "function") {
            checkPanelsOK(snap.segmentIndex, this.setPanelsOK, this.arePanelsOK);
          }
        }
        this.mouseMoved = false;
      } else {
        this.stopDrawing();
        removeGrid();
        this.mouseMoved = false;
      }
      this.panelDragSnapshot = null;
    }
    if (!this.mouseMoved) {
      this.clearPanelDragGrid();
      this.panelDragSnapshot = null;
    }

    if (Array.isArray(this.shape)) {
      this.shape = null;
    }
    this.positions = null;
    if (this.getDrawingMode() === DrawingModes.NONE) {
      this.isDrawingStarted = false;
    }

    if (this.getDrawingMode() === DrawingModes.TRIM) {
      this.finishTrimSphere();
    }

  }

  handleDoubleClick() {
    // Orientation changes are intentionally disabled on double-click.
    // Panels can only change orientation via SolarPanelSettings.
    return;
  }

  // Stop drawing process
  stopDrawing = () => {
    this.isDrawingStarted = false;
    this.updateDrawingMode(DrawingModes.NONE);
    this.cancelTrimSphere();
  }

  // Get normalized mouse position in device coordinates
  // Get normalized mouse position in device coordinates
getMousePosition(event) {
  const rect = this.sceneManager?.renderer?.domElement?.getBoundingClientRect();

  if (rect) {
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    this.mousePosition.x = x * 2 - 1;
    this.mousePosition.y = -(y * 2 - 1);
  } else {
    this.mousePosition.x = (event.clientX / window.innerWidth) * 2 - 1;
    const offsetY = event.clientY - 92.8;
    this.mousePosition.y = -(offsetY / this.sceneManager.height) * 2 + 1;
  }

  //  expose latest mouse position for paste logic
  VARS.mouseNDC = {
    x: this.mousePosition.x,
    y: this.mousePosition.y,
  };
}

  // get normalized device coordinates
  getNDC(object) {
    let position = object.getWorldPosition(new THREE.Vector3());
    let projection = position.clone().project(this.sceneManager.camera);
    return new THREE.Vector2(projection.x, projection.y);
  }

  // Get a point position for drawing shapes
  getPoints(point) {
    // since it doesn't work with this.mousePosition we use this
    point.x = this.mousePosition.x;
    point.y = this.mousePosition.y;
    return point;
  }

  // Get intersection points with 3D objects
  getIntersections() {
    let rayCaster = new THREE.Raycaster();
    rayCaster.setFromCamera(this.mousePosition, this.sceneManager.camera);
    return rayCaster.intersectObjects(this.Mesh, false);
  }

  startTrimSphere(point) {
    if (!point) return;

    this.cancelTrimSphere();
    this.isDrawingStarted = true;
    this.trimStartPoint = point.clone();

    this.trimSphere = createTrimSphereMesh({
      position: this.trimStartPoint,
      scale: { x: 0.1, y: 0.1, z: 0.1 },
      locked: false,
      enabled: true,
    });
    this.sceneManager.scene.add(this.trimSphere);
  }

  updateTrimSphere(point) {
    if (!this.trimSphere || !this.trimStartPoint || !point) return;
    const radius = Math.max(0.1, this.trimStartPoint.distanceTo(point));
    this.trimSphere.scale.setScalar(radius);
  }

  finishTrimSphere() {
    if (!this.trimSphere) return;

    const sphere = this.trimSphere;
    this.trimSphere = null;
    this.trimStartPoint = null;
    this.isDrawingStarted = false;

    TREESPHERES.spheres.push(sphere);
    this.setRemovedSections?.((prev) => ({
      ...prev,
      spheres: [...(prev?.spheres || []), sphere],
    }));
    window.attachControlsToSphere?.(sphere);
    notifyIrradianceNeedsRecompute("trim-sphere-created");
    this.updateDrawingMode(DrawingModes.NONE);
  }

  cancelTrimSphere() {
    if (!this.trimSphere) return;
    this.sceneManager.scene.remove(this.trimSphere);
    this.trimSphere.geometry?.dispose?.();
    this.trimSphere.material?.dispose?.();
    this.trimSphere = null;
    this.trimStartPoint = null;
  }
}
